/**
 * Shared color palette used throughout the application
 * for labels, user avatars, and widgets
 */

export const COLOR_PALETTE = [
  { tailwind: 'bg-gray-400', hex: '#9ca3af' },
  { tailwind: 'bg-red-500', hex: '#ef4444' },
  { tailwind: 'bg-orange-500', hex: '#f97316' },
  { tailwind: 'bg-amber-500', hex: '#f59e0b' },
  { tailwind: 'bg-yellow-500', hex: '#eab308' },
  { tailwind: 'bg-lime-500', hex: '#84cc16' },
  { tailwind: 'bg-green-500', hex: '#22c55e' },
  { tailwind: 'bg-emerald-500', hex: '#10b981' },
  { tailwind: 'bg-teal-500', hex: '#14b8a6' },
  { tailwind: 'bg-cyan-500', hex: '#06b6d4' },
  { tailwind: 'bg-sky-500', hex: '#0ea5e9' },
  { tailwind: 'bg-blue-500', hex: '#3b82f6' },
  { tailwind: 'bg-indigo-500', hex: '#6366f1' },
  { tailwind: 'bg-violet-500', hex: '#8b5cf6' },
  { tailwind: 'bg-purple-500', hex: '#a855f7' },
  { tailwind: 'bg-fuchsia-500', hex: '#d946ef' },
  { tailwind: 'bg-pink-500', hex: '#ec4899' },
  { tailwind: 'bg-rose-500', hex: '#f43f5e' },
  // Pastel colors
  { tailwind: 'bg-red-200', hex: '#fecaca' },
  { tailwind: 'bg-orange-200', hex: '#fed7aa' },
  { tailwind: 'bg-amber-200', hex: '#fde68a' },
  { tailwind: 'bg-yellow-200', hex: '#fef08a' },
  { tailwind: 'bg-lime-200', hex: '#d9f99d' },
  { tailwind: 'bg-green-200', hex: '#bbf7d0' },
  { tailwind: 'bg-emerald-200', hex: '#a7f3d0' },
  { tailwind: 'bg-teal-200', hex: '#99f6e4' },
  { tailwind: 'bg-cyan-200', hex: '#a5f3fc' },
  { tailwind: 'bg-sky-200', hex: '#bae6fd' },
  { tailwind: 'bg-blue-200', hex: '#bfdbfe' },
  { tailwind: 'bg-indigo-200', hex: '#c7d2fe' },
  { tailwind: 'bg-violet-200', hex: '#ddd6fe' },
  { tailwind: 'bg-purple-200', hex: '#e9d5ff' },
  { tailwind: 'bg-fuchsia-200', hex: '#f5d0fe' },
  { tailwind: 'bg-pink-200', hex: '#fbcfe8' },
  { tailwind: 'bg-rose-200', hex: '#fecdd3' },
] as const;

export const AVATAR_COLOR_PALETTE = [
  { name: 'Mint', hex: '#f0fdf9' },
  { name: 'Lilac', hex: '#faf5ff' },
  { name: 'Sky', hex: '#f0f9ff' },
  { name: 'Blush', hex: '#fef2f2' },
  { name: 'Canary', hex: '#fefce8' },
  { name: 'Slate', hex: '#f1f5f9' },
] as const;

export const COLOR_PALETTE_HEX = COLOR_PALETTE.map(c => c.hex);
export const COLOR_PALETTE_TAILWIND = COLOR_PALETTE.map(c => c.tailwind);
export const AVATAR_COLOR_PALETTE_HEX = AVATAR_COLOR_PALETTE.map(c => c.hex);

const LEGACY_AVATAR_NAME_TO_HEX: Record<string, string> = {
  mint: '#f0fdf9',
  lilac: '#faf5ff',
  sky: '#f0f9ff',
  blush: '#fef2f2',
  canary: '#fefce8',
  slate: '#f1f5f9',
};

const DEFAULT_AVATAR_COLOR_HEX = AVATAR_COLOR_PALETTE_HEX[AVATAR_COLOR_PALETTE_HEX.length - 1];

function toValidHexColor(rawColor: string | undefined | null): string | undefined {
  if (!rawColor) return undefined;

  const normalizedRaw = rawColor.trim().toLowerCase();
  if (!normalizedRaw) return undefined;

  if (LEGACY_AVATAR_NAME_TO_HEX[normalizedRaw]) {
    return LEGACY_AVATAR_NAME_TO_HEX[normalizedRaw];
  }

  if (normalizedRaw.startsWith('bg-')) {
    const converted = tailwindToHex(normalizedRaw);
    if (converted) {
      return converted.toLowerCase();
    }
  }

  if (/^#[0-9a-f]{6}$/.test(normalizedRaw)) {
    return normalizedRaw;
  }

  if (/^[0-9a-f]{6}$/.test(normalizedRaw)) {
    return `#${normalizedRaw}`;
  }

  return undefined;
}

function hexToRgb(hexColor: string): [number, number, number] {
  const normalized = hexColor.startsWith('#') ? hexColor.slice(1) : hexColor;
  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  return [red, green, blue];
}

function rgbDistance(fromHex: string, toHex: string): number {
  const [fromRed, fromGreen, fromBlue] = hexToRgb(fromHex);
  const [toRed, toGreen, toBlue] = hexToRgb(toHex);
  const redDistance = fromRed - toRed;
  const greenDistance = fromGreen - toGreen;
  const blueDistance = fromBlue - toBlue;
  return redDistance * redDistance + greenDistance * greenDistance + blueDistance * blueDistance;
}

export function normalizeAvatarColorHex(rawColor: string | undefined | null): string {
  const parsedHex = toValidHexColor(rawColor);
  if (!parsedHex) {
    return DEFAULT_AVATAR_COLOR_HEX;
  }

  if (AVATAR_COLOR_PALETTE_HEX.some(color => color === parsedHex)) {
    return parsedHex;
  }

  let closestHex = DEFAULT_AVATAR_COLOR_HEX;
  let closestDistance = Number.POSITIVE_INFINITY;

  for (const candidate of AVATAR_COLOR_PALETTE_HEX) {
    const distance = rgbDistance(parsedHex, candidate);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestHex = candidate;
    }
  }

  return closestHex;
}

export function getNextAvailableAvatarColor(usedColors: string[]): string {
  const normalizedUsedColors = new Set(usedColors.map(color => normalizeAvatarColorHex(color)));
  const unusedColor = AVATAR_COLOR_PALETTE_HEX.find(color => !normalizedUsedColors.has(color));

  if (unusedColor) {
    return unusedColor;
  }

  return AVATAR_COLOR_PALETTE_HEX[usedColors.length % AVATAR_COLOR_PALETTE_HEX.length];
}

export type ThemeHSLColor = {
  h: number;
  s: number;
  l: number;
};

/**
 * Convert a Tailwind color class (e.g., 'bg-blue-500') to hex color
 */
export function tailwindToHex(tailwindClass: string): string | undefined {
  const colorName = tailwindClass.replace('bg-', '');
  const color = COLOR_PALETTE.find(c => c.tailwind === tailwindClass || c.tailwind === `bg-${colorName}`);
  return color?.hex;
}

/**
 * Convert a hex color to Tailwind color class
 */
export function hexToTailwind(hexColor: string): string | undefined {
  const color = COLOR_PALETTE.find(c => c.hex.toLowerCase() === hexColor.toLowerCase());
  return color?.tailwind;
}

/**
 * Get a contrasting color from the palette for the given index
 * Uses a step pattern to maximize visual distinction
 */
export function getContrastingColor(index: number): string {
  // Skip gray (index 0) for better visual distinction
  const usableColors = COLOR_PALETTE_HEX.slice(1);
  
  // Use a larger step to maximize contrast between sequential assignments
  const step = Math.floor(usableColors.length / 3);
  const colorIndex = (index * step) % usableColors.length;
  
  return usableColors[colorIndex];
}

/**
 * Get the next available contrasting color not already used
 */
export function getNextAvailableColor(usedColors: string[]): string {
  // Skip gray for initial assignments
  const availableColors = COLOR_PALETTE_HEX.slice(1);
  
  // Find first color not in usedColors
  const unusedColor = availableColors.find(color => !usedColors.includes(color));
  
  // If all colors are used, fall back to contrasting pattern
  if (!unusedColor) {
    return getContrastingColor(usedColors.length);
  }
  
  return unusedColor;
}

/**
 * Convert hex color to HSL object
 */
export function hexToHSL(hex: string): { h: number; s: number; l: number } {
  // Remove # if present
  const cleanHex = hex.replace('#', '');
  
  // Convert hex to RGB
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function hslToHex(h: number, s: number, l: number): string {
  const hue = ((h % 360) + 360) % 360;
  const sat = Math.min(Math.max(s, 0), 100) / 100;
  const light = Math.min(Math.max(l, 0), 100) / 100;

  const chroma = (1 - Math.abs(2 * light - 1)) * sat;
  const huePrime = hue / 60;
  const x = chroma * (1 - Math.abs((huePrime % 2) - 1));

  let redPrime = 0;
  let greenPrime = 0;
  let bluePrime = 0;

  if (huePrime >= 0 && huePrime < 1) {
    redPrime = chroma;
    greenPrime = x;
  } else if (huePrime >= 1 && huePrime < 2) {
    redPrime = x;
    greenPrime = chroma;
  } else if (huePrime >= 2 && huePrime < 3) {
    greenPrime = chroma;
    bluePrime = x;
  } else if (huePrime >= 3 && huePrime < 4) {
    greenPrime = x;
    bluePrime = chroma;
  } else if (huePrime >= 4 && huePrime < 5) {
    redPrime = x;
    bluePrime = chroma;
  } else {
    redPrime = chroma;
    bluePrime = x;
  }

  const match = light - chroma / 2;
  const red = Math.round((redPrime + match) * 255);
  const green = Math.round((greenPrime + match) * 255);
  const blue = Math.round((bluePrime + match) * 255);

  const toHex = (value: number) => value.toString(16).padStart(2, '0');
  return `#${toHex(red)}${toHex(green)}${toHex(blue)}`;
}

export function getThemeChartPalette(themeColor?: ThemeHSLColor, size: number = 12): string[] {
  if (!themeColor) {
    return COLOR_PALETTE_HEX.slice(1, Math.max(2, size + 1));
  }

  const count = Math.max(1, size);
  const baseHue = ((themeColor.h % 360) + 360) % 360;
  const baseSaturation = Math.min(Math.max(themeColor.s, 45), 88);

  return Array.from({ length: count }, (_, index) => {
    const hue = (baseHue + index * (360 / count)) % 360;
    const saturation = Math.min(90, Math.max(45, baseSaturation - (index % 3) * 6));
    const lightness = 54 + ((index % 2) * 10 - 5);
    return hslToHex(hue, saturation, lightness);
  });
}
